/**
 * Geolocation utilities for worker auto-arrival detection.
 *
 * Uses navigator.geolocation.watchPosition to track the worker's GPS location
 * and calculates Haversine distance to the job site. When distance drops below
 * the arrival threshold, calls the onArrive callback so the UI can suggest
 * "mark as On Site".
 */

const ARRIVAL_RADIUS_METERS = 150;
const LEAVE_RADIUS_METERS = 300;

/**
 * Calculate the Haversine distance between two lat/lng points in meters.
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Start watching the worker's GPS position.
 *
 * @param {number} targetLat - Job site latitude
 * @param {number} targetLng - Job site longitude
 * @param {function} onArrive - Called when worker enters arrival radius
 * @param {function} onLeave - Called when worker leaves the site (optional)
 * @param {function} onError - Called on geolocation error
 * @returns {number|null} watchId - pass to stopGeoWatch to stop tracking
 */
export function startArrivalWatch(targetLat, targetLng, onArrive, onLeave, onError) {
  if (!navigator.geolocation) {
    if (onError) onError({ message: 'Geolocation not supported on this device' });
    return null;
  }

  let hasArrived = false;

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const { latitude, longitude } = position.coords;
      const dist = haversineDistance(latitude, longitude, targetLat, targetLng);

      if (dist <= ARRIVAL_RADIUS_METERS && !hasArrived) {
        hasArrived = true;
        if (onArrive) onArrive(dist);
      } else if (dist > LEAVE_RADIUS_METERS && hasArrived && onLeave) {
        hasArrived = false;
        onLeave(dist);
      }
    },
    (err) => {
      if (onError) onError(err);
    },
    {
      enableHighAccuracy: true,
      maximumAge: 10000,       // Accept cached position up to 10s old
      timeout: 30000,          // 30s timeout per reading
    }
  );

  return watchId;
}

/**
 * Stop GPS watching.
 */
/**
 * Fire-and-forget location capture — takes one fresh fix and posts it to
 * /worker/location. Called on every worker action (status change, clock,
 * message) and on app resume, so the pin tracks real movement even though
 * a backgrounded tab can't run a periodic heartbeat.
 */
export async function captureLocationNow() {
  if (!navigator.geolocation) return;
  try {
    const pos = await new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject,
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }));
    const api = (await import('../api/axios')).default;
    const payload = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
    const empId = JSON.parse(atob(localStorage.getItem('token').split('.')[1])).employee_id;
    if (empId) payload.employee_id = empId;
    await api.post('/worker/location', payload);
  } catch (_) { /* silent — opportunistic capture, never blocks the action */ }
}

export function stopGeoWatch(watchId) {
  if (watchId !== null && watchId !== undefined && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
  }
}
